import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { ShieldCheck, Play, CheckCircle, XCircle, Plus, Trash2, Loader2, Upload, Users, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompliancePage = () => {
  const { user, isHR } = useAuth();
  const [trainings, setTrainings] = useState([]);
  const [myStatus, setMyStatus] = useState([]);
  const [adminStatus, setAdminStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editTraining, setEditTraining] = useState(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizTraining, setQuizTraining] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizSubmitting, setQuizSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const promises = [
        axios.get(`${API_URL}/api/compliance/trainings`, { withCredentials: true }),
        axios.get(`${API_URL}/api/compliance/my-status`, { withCredentials: true }),
      ];
      if (isHR) promises.push(axios.get(`${API_URL}/api/compliance/admin-status`, { withCredentials: true }));
      const results = await Promise.allSettled(promises);
      if (results[0].status === 'fulfilled') setTrainings(results[0].value.data);
      if (results[1].status === 'fulfilled') setMyStatus(results[1].value.data);
      if (isHR && results[2]?.status === 'fulfilled') setAdminStatus(results[2].value.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [isHR]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveTraining = async () => {
    if (!editTraining) return;
    try {
      await axios.put(`${API_URL}/api/compliance/trainings/${editTraining.id}`, editTraining, { withCredentials: true });
      toast.success('Training updated');
      setEditTraining(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const addQuestion = () => {
    const questions = [...(editTraining.quiz_questions || [])];
    questions.push({
      id: `q${Date.now()}`, question: '', options: ['', '', '', ''], correct_answer: '0',
    });
    setEditTraining({ ...editTraining, quiz_questions: questions });
  };

  const openQuiz = (training) => {
    setQuizTraining(training);
    setQuizAnswers({});
    setQuizResult(null);
    setQuizOpen(true);
  };

  const handleSubmitQuiz = async () => {
    if (!quizTraining) return;
    const questions = quizTraining.quiz_questions || [];
    const unanswered = questions.filter(q => !(q.id in quizAnswers));
    if (unanswered.length > 0) {
      toast.error(`Please answer all ${questions.length} questions`);
      return;
    }
    setQuizSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/compliance/submit-quiz`, {
        training_id: quizTraining.id, answers: quizAnswers,
      }, { withCredentials: true });
      setQuizResult(res.data);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit quiz');
    } finally {
      setQuizSubmitting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="compliance-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-emerald-500" /> Compliance Training</h1>
        <p className="text-muted-foreground">Mandatory trainings for all employees. Pass percentage: 80%</p>
      </div>

      {isHR ? (
        <Tabs defaultValue="trainings">
          <TabsList>
            <TabsTrigger value="trainings" className="gap-2"><BookOpen className="w-4 h-4" /> Trainings</TabsTrigger>
            <TabsTrigger value="status" className="gap-2"><Users className="w-4 h-4" /> Employee Status</TabsTrigger>
          </TabsList>
          <TabsContent value="trainings" className="mt-4">{renderTrainings()}</TabsContent>
          <TabsContent value="status" className="mt-4">{renderAdminStatus()}</TabsContent>
        </Tabs>
      ) : renderEmployeeView()}

      {/* Edit Training Dialog (Admin) */}
      <Dialog open={!!editTraining} onOpenChange={(open) => !open && setEditTraining(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Training — {editTraining?.name}</DialogTitle>
            <DialogDescription>Configure video, quiz questions, and status</DialogDescription>
          </DialogHeader>
          {editTraining && (
            <div className="space-y-4">
              <div className="space-y-2"><Label>Name</Label><Input value={editTraining.name} onChange={(e) => setEditTraining({ ...editTraining, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Description</Label><Textarea value={editTraining.description || ''} onChange={(e) => setEditTraining({ ...editTraining, description: e.target.value })} rows={2} /></div>
              <div className="space-y-2"><Label>Video URL</Label><Input value={editTraining.video_url || ''} onChange={(e) => setEditTraining({ ...editTraining, video_url: e.target.value })} placeholder="YouTube or video URL" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Pass %</Label><Input type="number" value={editTraining.pass_percentage} onChange={(e) => setEditTraining({ ...editTraining, pass_percentage: parseInt(e.target.value) || 80 })} /></div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={editTraining.status} onChange={(e) => setEditTraining({ ...editTraining, status: e.target.value })}>
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Quiz Questions</Label>
                  <Button variant="outline" size="sm" className="gap-1" onClick={addQuestion}><Plus className="w-3 h-3" /> Add Question</Button>
                </div>
                {(editTraining.quiz_questions || []).map((q, qIdx) => (
                  <div key={qIdx} className="p-3 rounded-lg border bg-muted/20 space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Q{qIdx + 1}</Label>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => {
                        const qs = editTraining.quiz_questions.filter((_, i) => i !== qIdx);
                        setEditTraining({ ...editTraining, quiz_questions: qs });
                      }}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                    <Input value={q.question} onChange={(e) => { const qs = [...editTraining.quiz_questions]; qs[qIdx] = { ...qs[qIdx], question: e.target.value }; setEditTraining({ ...editTraining, quiz_questions: qs }); }} placeholder="Question text" />
                    {q.options.map((opt, oIdx) => (
                      <div key={oIdx} className="flex items-center gap-2">
                        <input type="radio" name={`correct_${qIdx}`} checked={String(q.correct_answer) === String(oIdx)} onChange={() => { const qs = [...editTraining.quiz_questions]; qs[qIdx] = { ...qs[qIdx], correct_answer: String(oIdx) }; setEditTraining({ ...editTraining, quiz_questions: qs }); }} />
                        <Input value={opt} onChange={(e) => { const qs = [...editTraining.quiz_questions]; const opts = [...qs[qIdx].options]; opts[oIdx] = e.target.value; qs[qIdx] = { ...qs[qIdx], options: opts }; setEditTraining({ ...editTraining, quiz_questions: qs }); }} placeholder={`Option ${oIdx + 1}`} className="text-sm h-8" />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditTraining(null)}>Cancel</Button>
                <Button onClick={handleSaveTraining}>Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quiz Dialog */}
      <Dialog open={quizOpen} onOpenChange={(open) => { if (!open) { setQuizOpen(false); setQuizResult(null); } }}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{quizTraining?.name} — Quiz</DialogTitle>
            <DialogDescription>Pass percentage: {quizTraining?.pass_percentage || 80}%</DialogDescription>
          </DialogHeader>
          {quizResult ? (
            <div className="text-center space-y-4 py-4">
              {quizResult.passed ? (
                <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto" />
              ) : (
                <XCircle className="w-16 h-16 text-red-500 mx-auto" />
              )}
              <h3 className="text-xl font-bold">{quizResult.passed ? 'Congratulations!' : 'Not Passed'}</h3>
              <p className="text-lg font-mono">{quizResult.score}% ({quizResult.correct}/{quizResult.total} correct)</p>
              <p className="text-sm text-muted-foreground">Pass percentage: {quizResult.pass_percentage}%</p>
              {quizResult.witties_awarded > 0 && (
                <div className="mx-auto inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300" data-testid="witties-awarded-banner">
                  <span className="text-2xl">🏆</span>
                  <span className="font-semibold">+{quizResult.witties_awarded} Witties earned</span>
                  <span className="text-xs opacity-80">— first-attempt pass</span>
                </div>
              )}
              {quizResult.passed && quizResult.witties_awarded === 0 && (
                <p className="text-xs text-muted-foreground italic">Witties are awarded only on the first attempt — keep going!</p>
              )}
              {quizResult.passed && (
                <p className="text-xs text-emerald-700 dark:text-emerald-400">Training auto-completed and recorded.</p>
              )}
              {!quizResult.passed && <Button onClick={() => { setQuizResult(null); setQuizAnswers({}); }}>Try Again</Button>}
            </div>
          ) : (
            <div className="space-y-4">
              {(quizTraining?.quiz_questions || []).map((q, qIdx) => (
                <div key={qIdx} className="p-3 rounded-lg border space-y-2" data-testid={`quiz-q-${qIdx}`}>
                  <p className="font-medium text-sm">Q{qIdx + 1}. {q.question}</p>
                  {q.options.map((opt, oIdx) => (
                    <label key={oIdx} className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${String(quizAnswers[q.id]) === String(oIdx) ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/50'}`}>
                      <input type="radio" name={`answer_${q.id}`} checked={String(quizAnswers[q.id]) === String(oIdx)} onChange={() => setQuizAnswers({ ...quizAnswers, [q.id]: String(oIdx) })} />
                      <span className="text-sm">{opt}</span>
                    </label>
                  ))}
                </div>
              ))}
              <Button className="w-full gap-2" onClick={handleSubmitQuiz} disabled={quizSubmitting} data-testid="submit-quiz-btn">
                {quizSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Submit Quiz
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  function renderTrainings() {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {trainings.map(t => (
          <Card key={t.id} data-testid={`training-${t.code}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <Badge className={t.status === 'active' ? 'badge-success' : 'bg-muted text-muted-foreground'}>{t.status}</Badge>
                <Badge variant="outline" className="font-mono">{t.code}</Badge>
              </div>
              <CardTitle className="text-base mt-2">{t.name}</CardTitle>
              <CardDescription>{t.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-xs text-muted-foreground">
                {t.video_url ? <span className="text-emerald-600">Video uploaded</span> : <span className="text-amber-600">No video yet</span>}
                {' | '}
                {(t.quiz_questions || []).length} quiz question(s)
                {' | '}
                Pass: {t.pass_percentage || 80}%
              </div>
              <Button size="sm" variant="outline" className="w-full gap-2" onClick={() => setEditTraining(JSON.parse(JSON.stringify(t)))} data-testid={`edit-training-${t.code}`}>
                <Upload className="w-3.5 h-3.5" /> Edit Training
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  function renderAdminStatus() {
    if (!adminStatus) return null;
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Employee Training Completion</CardTitle>
          <CardDescription>Track which employees have completed each training</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  {adminStatus.trainings.map(t => <th key={t.id}>{t.code}</th>)}
                </tr>
              </thead>
              <tbody>
                {adminStatus.employees.map(emp => (
                  <tr key={emp.employee_id}>
                    <td className="font-medium text-sm">{emp.name}</td>
                    <td className="text-sm text-muted-foreground">{emp.department}</td>
                    {adminStatus.trainings.map(t => {
                      const s = emp.trainings[t.id];
                      return (
                        <td key={t.id}>
                          {s?.completed ? (
                            <Badge className="badge-success text-xs gap-1"><CheckCircle className="w-3 h-3" /> {s.score}%</Badge>
                          ) : s?.attempts > 0 ? (
                            <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-xs">{s.score}% (fail)</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Pending</Badge>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  }

  function renderEmployeeView() {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {myStatus.map(t => (
          <Card key={t.id} className={t.completed ? 'border-emerald-200 dark:border-emerald-800' : ''} data-testid={`my-training-${t.code}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                {t.completed ? (
                  <Badge className="badge-success gap-1"><CheckCircle className="w-3 h-3" /> Completed</Badge>
                ) : (
                  <Badge variant="outline">Pending</Badge>
                )}
                <Badge variant="outline" className="font-mono">{t.code}</Badge>
              </div>
              <CardTitle className="text-base mt-2">{t.name}</CardTitle>
              <CardDescription>{t.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {t.video_url && (
                <Button variant="outline" size="sm" className="w-full gap-2" onClick={() => window.open(t.video_url, '_blank')}>
                  <Play className="w-3.5 h-3.5" /> Watch Training Video
                </Button>
              )}
              {t.completed ? (
                <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/10 rounded-lg">
                  <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Score: {t.score}%</p>
                  <p className="text-xs text-muted-foreground">Completed on {new Date(t.completed_at).toLocaleDateString('en-IN')}</p>
                </div>
              ) : (t.quiz_questions || []).length > 0 ? (
                <Button size="sm" className="w-full gap-2" onClick={() => openQuiz(t)} data-testid={`take-quiz-${t.code}`}>
                  <BookOpen className="w-3.5 h-3.5" /> Take Quiz ({t.quiz_questions.length} questions)
                </Button>
              ) : (
                <p className="text-xs text-center text-muted-foreground">Quiz not yet configured by admin</p>
              )}
              {t.attempts > 0 && !t.completed && (
                <p className="text-xs text-center text-amber-600">Last score: {t.score}% | Attempts: {t.attempts}</p>
              )}
            </CardContent>
          </Card>
        ))}
        {myStatus.length === 0 && <p className="text-muted-foreground col-span-3 text-center py-8">No active trainings at this time.</p>}
      </div>
    );
  }
};

export default CompliancePage;
